import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileClock, Check, Undo2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KIND = {
  changed: { label: "Changed", cls: "border-amber-300 bg-amber-100 text-amber-900" },
  new: { label: "New answer", cls: "border-sky-300 bg-sky-100 text-sky-900" },
};

export function OnboardingChangeChip({ change, onClick }) {
  const k = KIND[change.kind] || KIND.changed;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 transition-colors hover:brightness-95 ${k.cls}`}
      title={`Client's onboarding answer ${change.kind === "new" ? "is new" : "changed"}: ${change.new_display}`}
      data-testid={`onboarding-change-chip-${change.field_key}`}
    >
      <FileClock className="h-2.5 w-2.5" />
      {change.kind === "new" ? "new onboarding answer" : "onboarding changed"}
    </button>
  );
}

export function OnboardingChangesBanner({ changes, finalised, onReview }) {
  const n = changes.length;
  const plural = n === 1 ? "" : "s";
  return (
    <Card
      className="p-3 border border-amber-300 bg-amber-50 text-sm flex items-start gap-2"
      role="status"
      data-testid="onboarding-changes-banner"
    >
      <FileClock className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 text-amber-950">
        {finalised ? (
          <>Client updated <b data-testid="onboarding-changes-count">{n}</b> onboarding answer{plural} after this assessment was finalised.</>
        ) : (
          <><b data-testid="onboarding-changes-count">{n}</b> onboarding answer{plural} changed since this assessment was pre-filled. Nothing has been replaced — review each one.</>
        )}
      </div>
      <Button size="sm" variant="outline" className="h-7 border-amber-400 bg-white" onClick={onReview}
        data-testid="onboarding-changes-review-btn">
        {finalised ? "View changes" : "Review"}
      </Button>
    </Card>
  );
}

function ValueBox({ title, text, testid, tag }) {
  return (
    <div className="rounded border border-zinc-200 bg-white p-2 min-w-0">
      <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
        {title}
        {tag && <span className="normal-case tracking-normal text-2xs px-1 rounded bg-violet-100 text-violet-900">{tag}</span>}
      </div>
      <div className="text-xs text-zinc-900 whitespace-pre-wrap break-words" data-testid={testid}>
        {text || <span className="italic text-zinc-400">— blank —</span>}
      </div>
    </div>
  );
}

function ChangeRow({ change, finalised, busy, onAction }) {
  const k = KIND[change.kind] || KIND.changed;
  const fk = change.field_key;
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 space-y-2" data-testid={`onboarding-change-row-${fk}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-medium text-sm text-zinc-900">{change.field_label}</div>
        <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${k.cls}`}>{k.label}</span>
        <div className="text-2xs text-zinc-500">{change.module_label} · {change.section_label}</div>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <ValueBox title="Previous onboarding answer" text={change.previous_display} testid={`onboarding-change-prev-${fk}`} />
        <ValueBox title="New onboarding answer" text={change.new_display} testid={`onboarding-change-new-${fk}`} />
        <ValueBox title="In this assessment now" text={change.current_display} testid={`onboarding-change-current-${fk}`}
          tag={change.clinician_edited ? "edited by clinician" : null} />
      </div>
      {!finalised && (
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => onAction([fk], "keep")}
            data-testid={`onboarding-change-keep-${fk}`}>
            <Undo2 className="h-3.5 w-3.5 mr-1" />Keep current
          </Button>
          <Button size="sm" className="h-7" disabled={busy} onClick={() => onAction([fk], "apply")}
            data-testid={`onboarding-change-apply-${fk}`}>
            <Check className="h-3.5 w-3.5 mr-1" />Use new answer
          </Button>
        </div>
      )}
    </div>
  );
}

export function OnboardingChangesDialog({ open, onOpenChange, assessmentId, changes, finalised, onResolved }) {
  const [busy, setBusy] = useState(false);

  async function act(fieldKeys, action) {
    setBusy(true);
    try {
      const r = await api.post(`/assessments/${assessmentId}/onboarding-changes/resolve`, {
        items: fieldKeys.map((field_key) => ({ field_key, action })),
      });
      onResolved(r.data.assessment);
      const n = fieldKeys.length;
      toast.success(action === "apply"
        ? `Applied ${n} new onboarding answer${n === 1 ? "" : "s"}`
        : `Kept current value${n === 1 ? "" : "s"} for ${n} field${n === 1 ? "" : "s"}`);
      if ((r.data.assessment?.onboarding_changes || []).length === 0) onOpenChange(false);
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  const all = changes.map((c) => c.field_key);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="onboarding-changes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileClock className="h-5 w-5 text-amber-700" />
            Onboarding changes
          </DialogTitle>
          <DialogDescription>
            {finalised
              ? "This assessment is finalised, so values can't be changed. Start a reassessment to use the new answers."
              : "The client resubmitted onboarding. Choose whether each assessment field should use the new answer. Every decision is recorded in the audit trail."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {changes.map((c) => (
            <ChangeRow key={c.field_key} change={c} finalised={finalised} busy={busy} onAction={act} />
          ))}
        </div>
        <DialogFooter className="gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-zinc-500 self-center" />}
          {finalised ? (
            <Button variant="outline" disabled={busy || !all.length} onClick={() => act(all, "keep")}
              data-testid="onboarding-changes-dismiss-all">
              Dismiss notice
            </Button>
          ) : (
            <>
              <Button variant="outline" disabled={busy || !all.length} onClick={() => act(all, "keep")}
                data-testid="onboarding-changes-keep-all">
                Keep all current
              </Button>
              <Button disabled={busy || !all.length} onClick={() => act(all, "apply")}
                data-testid="onboarding-changes-apply-all">
                Use all new answers
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
